import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/feature/PageHeader";
import { testimonials } from "@/mocks/comics";

const principles = [
  { icon: "ri-thumb-up-line", title: "只推能看的爽", desc: "每条推荐都由编辑实际试看把关，无脑爽漫不注水、不灌水，保证打开就有爽点。" },
  { icon: "ri-medal-2-line", title: "榜单够真实", desc: "热血战斗排行综合多维度数据生成，客观反映作品当下人气，不做人工控评。" },
  { icon: "ri-user-heart-line", title: "尊重每一位读者", desc: "认真对待每一条追漫反馈，让分类更精准、推荐更懂你，做有温度的漫画导航。" },
];

const philosophy = [
  { num: "01", title: "聚合优质爽漫", desc: "把散落各处的热血战斗、开挂主角、逆袭流与打脸爽文漫聚合起来，帮读者省下翻找的时间。" },
  { num: "02", title: "用榜单说话", desc: "用真实的数据和口碑说话，让好作品被看见，让读者跟着榜单就能闭眼开刷。" },
  { num: "03", title: "一起越做越好", desc: "欢迎读者投稿与反馈，让炮渣漫画网真正成为每一位追漫人的宝藏导航。" },
];

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于炮渣漫画网",
  description: "炮渣漫画网定位为无脑爽漫推荐与热血战斗排行榜单门户，致力于帮读者快速找到开挂主角、逆袭流、龙傲天与打脸爽文漫。",
  inLanguage: "zh-CN",
};

export default function About() {
  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }} />

      <Navbar />
      <main>
        <PageHeader
          crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
          title="关于炮渣漫画网"
          subtitle="一个专注无脑爽漫与热血战斗榜单的漫画导航门户，让好爽漫被更多人看见，让读者一刷到底。"
        />

        {/* 品牌介绍 */}
        <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <span className="text-xs font-semibold tracking-widest text-primary-400">ABOUT</span>
              <h2 className="mt-2 font-heading text-2xl md:text-4xl text-foreground-950">我们是谁</h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                炮渣漫画网诞生于一群热血漫迷的一次"吐槽"：为什么找一部看着爽的漫画这么难？翻来翻去全是套路和注水。
                于是我们决定亲手做一件事——把真正的无脑爽漫推荐、热血战斗排行、开挂主角漫画与逆袭流合集，
                用一个简单直接的榜单呈现出来，让每个想看爽的读者，点开就能速食开挂剧情，一口气看爽。
              </p>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
                从热血战斗到龙傲天，从逆袭流到打脸爽文，我们只关心一件事：这部漫，够不够爽。够爽，就上榜单推荐给你。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/ranking" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors">
                  看热血排行
                </Link>
                <Link to="/recommend" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-100 border border-background-300/60 px-6 py-2.5 text-sm font-semibold text-foreground-950 hover:border-primary-500/50 transition-colors">
                  逛爽漫推荐
                </Link>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img
                src="https://readdy.ai/api/search-image?query=dark%20fantasy%20anime%20illustration%20of%20a%20lone%20hero%20standing%20on%20a%20cliff%20overlooking%20a%20fiery%20sky%2C%20glowing%20embers%20floating%20in%20the%20air%2C%20dramatic%20orange%20and%20gold%20lighting%2C%20epic%20hot-blooded%20manga%20art%20with%20deep%20shadows%20and%20silhouette&width=1200&height=900&seq=about-fire&orientation=landscape"
                alt="炮渣漫画网热血清凉的氛围插画"
                className="w-full rounded-lg object-cover object-center border border-background-300/50"
              />
            </div>
          </div>
        </section>

        {/* 内容原则 */}
        <section className="bg-background-100 border-y border-background-300/50 py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="text-xs font-semibold tracking-widest text-accent-500">PRINCIPLES</span>
              <h2 className="mt-2 font-heading text-2xl md:text-4xl text-foreground-950">我们的内容原则</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {principles.map((p) => (
                <div key={p.title} className="rounded-lg bg-background-50 border border-background-300/50 p-6 text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-500/15 text-primary-400">
                    <i className={`${p.icon} text-2xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 发展理念 */}
        <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold tracking-widest text-primary-400">VISION</span>
            <h2 className="mt-2 font-heading text-2xl md:text-4xl text-foreground-950">我们的发展理念</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {philosophy.map((p) => (
              <div key={p.num} className="relative rounded-lg bg-background-100 border border-background-300/50 p-6">
                <span className="font-heading text-4xl md:text-5xl text-primary-500/25">{p.num}</span>
                <h3 className="mt-3 font-heading text-lg text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 口碑 */}
        <section className="bg-background-100 border-y border-background-300/50 py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="text-xs font-semibold tracking-widest text-accent-500">VOICE</span>
              <h2 className="mt-2 font-heading text-2xl md:text-4xl text-foreground-950">追漫人的声音</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-lg bg-background-50 border border-background-300/50 p-6">
                  <div className="flex items-center gap-0.5 text-accent-400 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="text-sm leading-relaxed text-foreground-600">“{t.content}”</blockquote>
                  <figcaption className="mt-4 text-sm">
                    <span className="font-semibold text-foreground-950">{t.name}</span>
                    <span className="text-foreground-600"> · {t.role}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* 联系 */}
        <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="rounded-lg bg-gradient-to-br from-primary-700/30 to-primary-900/20 border border-primary-500/30 p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">联系我们</h2>
                <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
                  有想推荐的爽漫、想吐槽的榜单，或是有任何建议，欢迎随时联系我们。
                </p>
                <ul className="mt-5 space-y-3 text-sm text-foreground-700">
                  <li className="flex items-center gap-2"><i className="ri-mail-line text-primary-400"></i>contact@paozhamanhua.com</li>
                  <li className="flex items-center gap-2"><i className="ri-wechat-line text-primary-400"></i>官方公众号：炮渣漫画网</li>
                  <li className="flex items-center gap-2"><i className="ri-time-line text-primary-400"></i>工作日 9:00 - 21:00</li>
                </ul>
              </div>
              <Link
                to="/faq"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm md:text-base font-semibold text-background-50 hover:bg-primary-600 transition-colors md:justify-self-end"
              >
                查看常见问题<i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}